/* eslint-disable  */
//@ts-nocheck
import { format } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { StreamChat } from 'stream-chat';
import {
  Channel,
  Chat,
  MessageInput,
  useChannelActionContext,
  useChannelStateContext,
  VirtualizedMessageList,
  useCreateChatClient,
} from 'stream-chat-react';
import 'stream-chat-react/dist/css/v2/index.css';
import styled from 'styled-components';
import './chat.css';
import auth from '../auth';
import { HStack, VStack } from '../common/stack';
import { Tag } from '../common/tag';
import { ChatAvatar } from './Avatar';
import { ReactComponent as ArrowRight } from '../../images/arrow-right.svg';

const boxPadding = 10;
const currentUserPadding = 5;
const totalPadding = boxPadding + currentUserPadding;

const MessageWrapper = styled.div`
  padding: 3px ${boxPadding}px;
`;

const Username = styled.span`
  font-size: 15px;
  font-weight: bold;
`;

const MessageAvatarPlaceholder = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 90px;
  background-color: #151e34;
  flex: 0 0 30px;
  color: #fff;
  font-size: 14px !important;
  font-weight: bold;
  text-align: center;
  vertical-align: middle;
  line-height: 30px;
`;

const ChatLayout = styled.div`
  width: 100%;
`;

const ChatContainer = styled.div`
  width: 100%;
  border-radius: 8px;
  border: 1px solid #e3e6ed;
  height: 450px;
`;

const LiveChatHeader = styled.div`
  display: flex;
  margin-bottom: 10px;
  align-items: center;
`;

const Title = styled.span`
  font-size: 14px;
  line-height: 21px;
  font-weight: bold;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const Description = styled.span`
  font-size: 12px;
  line-height: 18px;
  color: #b1b4bb;
`;

const MessageInputContainer = styled.div`
  margin-top: 10px;
  position: relative;
`;

const Input = styled.input.attrs({ placeholderTextColor: 'red' })`
  background: #ffffff;
  border: 1px solid #e3e6ed;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 10px !important;
  padding-right: 45px !important;
  ::placeholder {
    color: #151e34;
    opacity: 0.2;
  }
  &:focus {
    outline: none;
    border: 1px solid #b1b4bb;
  }
`;

const SendIcon = styled(ArrowRight)`
  width: 20px;
  height: 20px;
  float: right;
  margin-right: 10px;
  margin-top: -32px;
  position: relative;
  z-index: 2;
  cursor: pointer;
`;

const SendMessage = ({ ...props }) => {
  const [text, setText] = useState('');
  const { sendMessage } = useChannelActionContext();
  const send = useCallback(() => {
    if (text.length) {
      sendMessage({ text });
      setText('');
    }
  }, [text]);
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      send();
    }
  };
  return (
    <MessageInputContainer>
      <Input
        {...props}
        value={text}
        placeholder="Write your message..."
        onKeyPress={handleKeyPress}
        onChange={(e) => setText(e.target.value)}
      />
      <SendIcon alt="Send message" onClick={send}></SendIcon>
    </MessageInputContainer>
  );
};

export const AvatarPlaceholder = ({ firstLetter }) => (
  <MessageAvatarPlaceholder>{firstLetter}</MessageAvatarPlaceholder>
);

const getWatcherText = (watcherCount) => {
  const noun = watcherCount === 1 ? 'user' : 'users';
  return `${watcherCount} ${noun}`;
};

const LiveChat = ({ userId }) => {
  const { channel } = useChannelStateContext();
  // TODO: use GetStream SDK instead of long polling. SDK doesn't work.
  const watchersQuery = useQuery(
    ['watchersCount'],
    () =>
      channel.query({
        data: {},
      }),
    { enabled: !!channel, refetchInterval: 5000 },
  );

  return (
    <ChatLayout>
      <LiveChatHeader>
        <VStack spacing={3}>
          <HStack spacing={6}>
            <Title>Live chat</Title>
            <Tag color="#F13174">
              {getWatcherText(
                !watchersQuery.data ? 1 : watchersQuery.data.watcher_count,
              )}
            </Tag>
          </HStack>
          <Description>
            We show messages from the entire livestream history
          </Description>
        </VStack>
      </LiveChatHeader>
      <ChatContainer>
        <VirtualizedMessageList
          stickToBottomScrollBehavior="auto"
          defaultItemHeight={54}
          customMessageRenderer={(messages, index) => (
            <LiveStreamMessage userId={userId} {...messages[index]} />
          )}
        />
      </ChatContainer>
      <MessageInput />
    </ChatLayout>
  );
};

export const LiveStreamMessage = (props) => {
  const {
    userId: currentUserId,
    user: { id, image, name },
    created_at,
    text,
  } = props;

  const isMessageFromCurrentUser = id === currentUserId;
  return (
    <MessageWrapper currentUser={isMessageFromCurrentUser}>
      <div
        className="flex rounded-lg w-full"
        style={{
          padding: `${currentUserPadding}px`,
          border: `${
            isMessageFromCurrentUser
              ? '1px solid rgba(99, 103, 239, 0.45)'
              : 'none'
          }`,
          background: `${isMessageFromCurrentUser ? '#F2F4FD' : 'none'}`,
        }}
      >
        <div className="flex-none">
          <ChatAvatar src={image} username={name} />
        </div>
        <div className="flex flex-col ml-3 flex-1 w-full">
          <div className="flex items-center">
            <Username>{name}</Username>
            <span className="text-gray-500 text-xs ml-2">
              {format(new Date(created_at), 'MMM d, h:mmaa')}
            </span>
          </div>
          <div
            style={{ wordBreak: 'break-word' }}
            className="text-sm break-words max-w-full"
          >
            {text}
          </div>
        </div>
      </div>
    </MessageWrapper>
  );
};

const customClasses = {
  virtualMessage: 'custom-virtualMessage',
};

export const LiveStreamChat = ({ influencerId, influencer, streamToken }) => {
  const { user_id: userId } = jwtDecode(streamToken);

  const chatClient = useCreateChatClient({
    apiKey: import.meta.env.VITE_STREAM_CHAT_ID,
    userData: { id: userId },
    tokenOrProvider: streamToken,
  });

  const influencerAvatar = `${influencer?.mediaAvatar?.cdn01}${influencer?.mediaAvatar?.image}`;
  const influencerName = `${influencer.firstName} ${influencer.lastName}`;

  const [channel, setChannel] = useState(null);
  useEffect(() => {
    const initChat = async () => {
      try {
        if (!streamToken) return;

        await chatClient.connectUser(
          {
            id: userId,
            name: influencerName,
            image: influencerAvatar,
          },
          streamToken,
        );

        const ch = await chatClient.channel(
          'livestream',
          `live-stream-chat-${influencerId}`,
          {},
        );

        await ch.watch();

        setChannel(ch);
      } catch (err) {
        console.error(err);
        setChannel(null);
      }
    };

    chatClient && initChat();
    return () => {
      chatClient && chatClient.disconnectUser();
    };
  }, [influencerId, streamToken, chatClient]);

  if (!channel) {
    return <div>Loading chat...</div>;
  }

  return (
    <div id="livestream">
      <Chat client={chatClient} customClasses={customClasses} theme="light">
        <Channel channel={channel} Input={SendMessage}>
          <LiveChat userAvatar={influencerAvatar} userId={userId} />
        </Channel>
      </Chat>
    </div>
  );
};
